/**
 * Color conversion utilities for the color picker.
 * OKLCH (storage) ↔ RGB ↔ HSV (picker UI).
 * RGB from OKLCH is linear; we convert to sRGB for display.
 */

export interface OKLCH {
  l: number; // 0–1
  c: number; // 0–0.4 typical
  h: number; // 0–360
}

export interface RGB {
  r: number; // 0–1 linear or 0–255 sRGB depending on context
  g: number;
  b: number;
}

export interface HSV {
  h: number; // 0–360
  s: number; // 0–1
  v: number; // 0–1
}

/** Linear RGB (0–1) from OKLCH. Matches shader oklchToRgb. */
export function oklchToLinearRgb(l: number, c: number, h: number): { r: number; g: number; b: number } {
  const hRad = (h * Math.PI) / 180;
  const a = c * Math.cos(hRad);
  const b = c * Math.sin(hRad);
  const l_ = l + 0.3963377774 * a + 0.2158037573 * b;
  const m_ = l - 0.1055613458 * a - 0.0638541728 * b;
  const s_ = l - 0.0894841775 * a - 1.291485548 * b;
  const l3 = l_ * l_ * l_;
  const m3 = m_ * m_ * m_;
  const s3 = s_ * s_ * s_;
  const r = 4.0767416621 * l3 - 3.3077115913 * m3 + 0.2309699292 * s3;
  const g = -1.2684380046 * l3 + 2.6097574011 * m3 - 0.3413193965 * s3;
  const b_ = -0.0041960863 * l3 - 0.7034186147 * m3 + 1.707614701 * s3;
  return {
    r: Math.max(0, Math.min(1, r)),
    g: Math.max(0, Math.min(1, g)),
    b: Math.max(0, Math.min(1, b_))
  };
}

export function linearToSrgb(c: number): number {
  return c <= 0.0031308 ? 12.92 * c : 1.055 * Math.pow(c, 1 / 2.4) - 0.055;
}

function srgbToLinear(c: number): number {
  return c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
}

/** sRGB hex or rgb() string for CSS from OKLCH. */
export function oklchToCssRgb(l: number, c: number, h: number): string {
  const { r, g, b } = oklchToLinearRgb(l, c, h);
  const R = Math.round(linearToSrgb(r) * 255);
  const G = Math.round(linearToSrgb(g) * 255);
  const B = Math.round(linearToSrgb(b) * 255);
  return `rgb(${R},${G},${B})`;
}

/** RGB channels (0–1) to HSV (h 0–360, s/v 0–1). Works on linear or sRGB channels; pair with matching inverse. */
export function rgbChannelsToHsv(r: number, g: number, b: number): HSV {
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const d = max - min;
  const v = max;
  const s = max === 0 ? 0 : d / max;
  let h = 0;
  if (d !== 0) {
    if (max === r) h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
    else if (max === g) h = ((b - r) / d + 2) / 6;
    else h = ((r - g) / d + 4) / 6;
  }
  return { h: h * 360, s, v };
}

/** @deprecated Use rgbChannelsToHsv — same math; name kept for call sites. */
export function linearRgbToHsv(r: number, g: number, b: number): HSV {
  return rgbChannelsToHsv(r, g, b);
}

/** OKLCH to HSV via linear RGB (legacy). Prefer oklchToHsvForPicker for UI. */
export function oklchToHsv(l: number, c: number, h: number): HSV {
  const rgb = oklchToLinearRgb(l, c, h);
  return linearRgbToHsv(rgb.r, rgb.g, rgb.b);
}

/** sRGB (0–1) to HSV. Picker uses display-referred channels (same space as CSS / canvas). */
export function srgbToHsv(r: number, g: number, b: number): HSV {
  return rgbChannelsToHsv(r, g, b);
}

/** HSV to sRGB (0–1). Inverse of srgbToHsv — do not gamma-encode (would break picker round-trip). */
export function hsvToSrgb(h: number, s: number, v: number): { r: number; g: number; b: number } {
  return hsvToRgbChannels(h, s, v);
}

/** OKLCH → sRGB → HSV for picker open. Pointer position then matches canvas (sRGB). */
export function oklchToHsvForPicker(l: number, c: number, h: number): HSV {
  const linear = oklchToLinearRgb(l, c, h);
  const sr = linearToSrgb(linear.r);
  const sg = linearToSrgb(linear.g);
  const sb = linearToSrgb(linear.b);
  return srgbToHsv(sr, sg, sb);
}

/** HSV (from sRGB picker) → sRGB → linear → OKLCH for picker apply. Uses unclamped chroma so swatch matches. */
export function hsvSrgbToOklch(h: number, s: number, v: number): OKLCH {
  const srgb = hsvToSrgb(h, s, v);
  const rl = srgbToLinear(srgb.r);
  const gl = srgbToLinear(srgb.g);
  const bl = srgbToLinear(srgb.b);
  return linearRgbToOklchForPicker(rl, gl, bl);
}

/** sRGB (0–1) to CSS rgb() string. Use for picker swatch so it matches the canvas exactly. */
export function srgbToCssRgb(r: number, g: number, b: number): string {
  const R = Math.round(Math.max(0, Math.min(1, r)) * 255);
  const G = Math.round(Math.max(0, Math.min(1, g)) * 255);
  const B = Math.round(Math.max(0, Math.min(1, b)) * 255);
  return `rgb(${R},${G},${B})`;
}

/** HSV to RGB channels (0–1). Pair with rgbChannelsToHsv on the same space (sRGB for picker UI). */
export function hsvToRgbChannels(h: number, s: number, v: number): { r: number; g: number; b: number } {
  const H = ((h % 360) + 360) % 360 / 60;
  const C = v * s;
  const X = C * (1 - Math.abs((H % 2) - 1));
  const m = v - C;
  let r = 0, g = 0, b = 0;
  if (H < 1) { r = C; g = X; b = 0; }
  else if (H < 2) { r = X; g = C; b = 0; }
  else if (H < 3) { r = 0; g = C; b = X; }
  else if (H < 4) { r = 0; g = X; b = C; }
  else if (H < 5) { r = X; g = 0; b = C; }
  else { r = C; g = 0; b = X; }
  return { r: r + m, g: g + m, b: b + m };
}

/** HSV to linear RGB (0–1). For shader/legacy paths — not the display picker round-trip. */
export function hsvToLinearRgb(h: number, s: number, v: number): { r: number; g: number; b: number } {
  return hsvToRgbChannels(h, s, v);
}

/** Linear RGB to OKLCH. Inverse of oklchToLinearRgb (simplified: RGB → XYZ → Lab → LCH). Chroma clamped to 0.4 for UI sliders. */
export function linearRgbToOklch(r: number, g: number, b: number): OKLCH {
  return linearRgbToOklchInternal(r, g, b, true);
}

/** Like linearRgbToOklch but does not clamp chroma. Use for picker so stored OKLCH matches picked sRGB. */
function linearRgbToOklchForPicker(r: number, g: number, b: number): OKLCH {
  return linearRgbToOklchInternal(r, g, b, false);
}

function linearRgbToOklchInternal(
  r: number,
  g: number,
  b: number,
  clampChroma: boolean
): OKLCH {
  // Linear sRGB to OKLab (Bjorn Ottosson), inverse of oklchToLinearRgb path.
  const rl = Math.max(0, Math.min(1, r));
  const gl = Math.max(0, Math.min(1, g));
  const bl = Math.max(0, Math.min(1, b));
  const l = Math.cbrt(0.4122214708 * rl + 0.5363325363 * gl + 0.0514459929 * bl);
  const m = Math.cbrt(0.2119034982 * rl + 0.6806995451 * gl + 0.1073969566 * bl);
  const s = Math.cbrt(0.0883024619 * rl + 0.2817188376 * gl + 0.6299787005 * bl);
  const L = 0.2104542553 * l + 0.7936177850 * m - 0.0040720468 * s;
  const a = 1.9779984951 * l - 2.4285922050 * m + 0.4505937099 * s;
  const b_ = 0.0259040371 * l + 0.7827717662 * m - 0.8086757660 * s;
  // Lab to LCH
  const C = Math.sqrt(a * a + b_ * b_);
  let H = (Math.atan2(b_, a) * 180) / Math.PI;
  if (H < 0) H += 360;
  const chroma = clampChroma ? Math.max(0, Math.min(0.4, C)) : Math.max(0, C);
  return {
    l: Math.max(0, Math.min(1, L)),
    c: chroma,
    h: H
  };
}

/** HSV to OKLCH (for saving from picker). */
export function hsvToOklch(h: number, s: number, v: number): OKLCH {
  const rgb = hsvToLinearRgb(h, s, v);
  return linearRgbToOklch(rgb.r, rgb.g, rgb.b);
}
